import { useState, useEffect, useRef } from 'react'
import { getActorMovies, getMovieCast, getMovieCastFull, posterUrl, profileUrl } from '../../api/tmdb'
import { updateParty } from '../../utils/partyGame'
import Filmstrip from '../Filmstrip'
import { IconArrowRight, IconLock, IconClapper, IconTarget, IconStar, IconMasks } from '../icons'

const INACTIVITY_MS = 2 * 60 * 1000 // 2-minute Cameo timeout

const MOVIE_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='342' height='513' viewBox='0 0 342 513'%3E%3Crect fill='%23333' width='342' height='513'/%3E%3C/svg%3E"
const ACTOR_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='185' height='278' viewBox='0 0 185 278'%3E%3Crect fill='%23333' width='185' height='278'/%3E%3Ccircle fill='%23555' cx='92' cy='100' r='40'/%3E%3Cellipse fill='%23555' cx='92' cy='220' rx='60' ry='50'/%3E%3C/svg%3E"

// Host taunts — render as elegant italic film-quotes in the HOST screen
// (`.h2h-host__taunt` → serif italic, accent-hi). Emoji intentionally stripped
// from the strings so the visual treatment carries the personality, not the
// glyph noise. Wording preserved.
const TAUNTS = [
  'Are you SURE about that pick?',
  'Was he even in that movie?',
  'Oh she was GREAT in that one!',
  'Bold move...',
  'The other team is sweating right now',
  "Someone's about to make a very interesting choice",
  "I've seen this movie before... it doesn't end well",
  'No hints. No regrets.',
  'The Director has seen everything. Everything.',
  'This is either genius or disaster. Only one way to find out.',
]

/**
 * Party Mode H2H — turn-based team gameplay.
 *   role='host'   — TV: shows whose turn it is + turn queue + random taunts.
 *                   STRATEGIC INFO BLACKOUT — chains are hidden.
 *   role='active' — active player phone: movie/actor picker, sees OWN team chain.
 *   role='watch'  — non-active player: shows whose turn + their own team's chain.
 */
export default function PartyH2HGameplay({ party, currentUserId, role }) {
  const players  = party?.players ?? {}
  const teams    = Array.isArray(party?.teams) ? party.teams : []
  const goalActor   = party?.goalActor
  const startActor  = party?.startActor
  const h2hChains   = party?.h2hChains ?? {}

  const teamTurnIndices = party?.teamTurnIndices ?? {}

  // The player's OWN team is the source of truth for whose turn it is
  // (H2H is simultaneous racing — every team has its own active player).
  const myTeam = teams.find((t) => (t.playerIds || []).includes(currentUserId))
  const myTeamTurnIdx = teamTurnIndices[myTeam?.id] ?? 0
  const activeUidForMyTeam = myTeam?.playerIds?.[myTeamTurnIdx] ?? null
  const activePlayerForMyTeam = activeUidForMyTeam ? players[activeUidForMyTeam] : null

  // Trust the role prop from the parent (computed against this player's own team)
  const isMyTurn = role === 'active'

  // My team's chain
  const myChain = myTeam ? (h2hChains[myTeam.id] || []) : []
  const myLastActor = [...myChain].reverse().find((c) => c.type === 'actor') || startActor

  // ── Host taunts: rotate randomly every 30–60 seconds ──────────────────────
  const [tauntIdx, setTauntIdx] = useState(() => Math.floor(Math.random() * TAUNTS.length))
  useEffect(() => {
    if (role !== 'host') return
    const id = setTimeout(() => {
      setTauntIdx(Math.floor(Math.random() * TAUNTS.length))
    }, 30000 + Math.random() * 30000)
    return () => clearTimeout(id)
  }, [role, tauntIdx])

  // ── Tick clock for inactivity detection ───────────────────────────────────
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000)
    return () => clearInterval(id)
  }, [])

  const turnStartMs = party?.h2hTurnStartedAt?.toMillis?.()
                    ?? (party?.h2hTurnStartedAt?.seconds ? party.h2hTurnStartedAt.seconds * 1000 : null)
  const turnElapsedMs = turnStartMs ? Date.now() - turnStartMs : 0
  const showCameoPrompt = isMyTurn && role === 'active' && turnElapsedMs >= INACTIVITY_MS

  // ── HOST view — strategic blackout, all teams racing simultaneously ───────
  if (role === 'host') {
    return (
      <div className="h2h-host">
        <div className="h2h-host__header">
          <p className="h2h-host__matchup">
            <strong>{startActor?.name}</strong>
            <span className="h2h-host__matchup-arrow" aria-hidden="true">
              <IconArrowRight size={18} />
            </span>
            <strong>{goalActor?.name}</strong>
          </p>
        </div>

        <div className="h2h-host__turn">
          <p className="h2h-host__turn-label">TEAMS RACING</p>
          <div className="h2h-host__teams-grid">
            {teams.map((t) => {
              const tTurnIdx = teamTurnIndices[t.id] ?? 0
              const tActiveUid = t.playerIds?.[tTurnIdx]
              const tActiveP   = tActiveUid ? players[tActiveUid] : null
              const initial    = (tActiveP?.username || 'G').charAt(0).toUpperCase()
              return (
                <div key={t.id} className="h2h-host__team-active-card">
                  <div className="h2h-host__turn-avatar h2h-host__turn-avatar--glow">
                    {tActiveP?.avatar
                      ? <img src={tActiveP.avatar} alt="" />
                      : <span className="party-avatar__initial">{initial}</span>}
                  </div>
                  <p className="h2h-host__turn-team">{t.name}</p>
                  <p className="h2h-host__turn-name">{tActiveP?.username || 'Guest'} is up</p>
                </div>
              )
            })}
          </div>
        </div>

        <p className="h2h-host__taunt">{TAUNTS[tauntIdx]}</p>

        <div className="h2h-host__queue">
          <p className="h2h-host__queue-label">Team rosters</p>
          <div className="h2h-host__queue-strip">
            {teams.map((t) => {
              const tTurnIdx  = teamTurnIndices[t.id] ?? 0
              return (
                <div
                  key={t.id}
                  className="h2h-host__team-chip h2h-host__team-chip--active"
                >
                  <p className="h2h-host__team-chip-name">{t.name}</p>
                  <div className="h2h-host__team-chip-roster">
                    {(t.playerIds || []).map((uid, idx) => {
                      const p = players[uid]
                      const isUp = idx === tTurnIdx
                      const initial = (p?.username || 'G').charAt(0).toUpperCase()
                      return (
                        <div
                          key={uid}
                          className={`h2h-host__roster-avatar${isUp ? ' h2h-host__roster-avatar--up' : ''}`}
                          title={p?.username}
                        >
                          {p?.avatar
                            ? <img src={p.avatar} alt="" />
                            : <span className="party-avatar__initial">{initial}</span>}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <p className="h2h-host__blackout">
          <IconLock size={14} aria-hidden="true" />
          <span>Chains hidden — strategic blackout</span>
        </p>
      </div>
    )
  }

  // ── WATCHING player ───────────────────────────────────────────────────────
  // Use the role prop exclusively (set by parent against THIS player's team's
  // turn pointer — H2H runs every team simultaneously). The watch view shows
  // the watcher's OWN team's currently-active player, not a globally-active one.
  if (role !== 'active') {
    return (
      <div className="h2h-watch">
        <div className="h2h-watch__header">
          <div className="h2h-watch__avatar">
            {activePlayerForMyTeam?.avatar
              ? <img src={activePlayerForMyTeam.avatar} alt="" />
              : <IconClapper size={20} />}
          </div>
          <p className="h2h-watch__title">
            <strong>{myTeam?.name || 'Your team'}</strong> · {activePlayerForMyTeam?.username || 'Guest'} is up
          </p>
        </div>

        {myTeam && (
          <>
            <p className="h2h-watch__team-label">Your team's chain</p>
            {myChain.length > 0
              ? <div className="h2h-watch__chain"><Filmstrip chain={myChain} onItemClick={() => {}} hidePlaceholder followLiveEdge /></div>
              : <p className="h2h-watch__chain-empty">Nothing yet — your turn comes around.</p>}
          </>
        )}

        <div className="h2h-watch__goal">
          <p className="h2h-watch__goal-label"><IconTarget size={15} style={{ verticalAlign: 'middle' }} /> Goal</p>
          <p className="h2h-watch__goal-name">{goalActor?.name}</p>
        </div>

        <p className="h2h-watch__pulse">Stand by — your turn's coming...</p>
      </div>
    )
  }

  // ── ACTIVE player ─────────────────────────────────────────────────────────
  return (
    <ActiveTurn
      party={party}
      currentUserId={currentUserId}
      players={players}
      teams={teams}
      myTeam={myTeam}
      myTeamTurnIdx={myTeamTurnIdx}
      teamTurnIndices={teamTurnIndices}
      myChain={myChain}
      myLastActor={myLastActor}
      goalActor={goalActor}
      showCameoPrompt={showCameoPrompt}
    />
  )
}

// ── Active turn — picks a movie, then an actor, then passes torch to next
// player WITHIN THIS TEAM ONLY. Other teams race in parallel — we never
// touch their turn pointers.
function ActiveTurn({
  party, currentUserId, players, teams, myTeam, myTeamTurnIdx, teamTurnIndices,
  myChain, myLastActor, goalActor, showCameoPrompt,
}) {
  const [view, setView]               = useState('movies')
  const [movies, setMovies]           = useState([])
  const [cast, setCast]               = useState([])
  const [selectedMovie, setSelected]  = useState(null)
  const [loading, setLoading]         = useState(false)
  const [error, setError]             = useState('')
  const [committing, setCommitting]   = useState(false)
  const [cameoLoading, setCameoLoading] = useState(false)

  const usedActorIds = new Set(myChain.filter(c => c.type === 'actor').map(c => c.id))
  const usedMovieIds = new Set(myChain.filter(c => c.type === 'movie').map(c => c.id))

  // Reset to movies view when new turn arrives
  const lastActorIdRef = useRef(myLastActor?.id)
  useEffect(() => {
    if (myLastActor?.id !== lastActorIdRef.current) {
      lastActorIdRef.current = myLastActor?.id
      setView('movies')
      setSelected(null)
      setCast([])
    }
  }, [myLastActor?.id])

  // Load filmography
  useEffect(() => {
    if (!myLastActor?.id || view !== 'movies') return
    setLoading(true); setError('')
    getActorMovies(myLastActor.id)
      .then((m) => setMovies(m))
      .catch(() => setError('Could not load movies'))
      .finally(() => setLoading(false))
  }, [myLastActor?.id, view])

  // Goal-actor injection (same as Co-Op)
  const injectGoalActor = async (castArr, movieId) => {
    if (!goalActor) return castArr
    if (castArr.some((c) => c.id === goalActor.id)) return castArr
    try {
      const full = await getMovieCastFull(movieId)
      const entry = full.find((c) => c.id === goalActor.id)
      if (!entry) return castArr
      return [...castArr.slice(0, Math.max(0, castArr.length - 1)), entry]
    } catch { return castArr }
  }

  const handleMovieTap = async (movie) => {
    if (usedMovieIds.has(movie.id)) return
    setSelected(movie); setView('cast'); setLoading(true); setError('')
    try {
      const c = await getMovieCast(movie.id)
      setCast(await injectGoalActor(c, movie.id))
    } catch { setError('Could not load cast') }
    finally { setLoading(false) }
  }

  // Advance turn within THIS team only. Other teams maintain their own
  // independent pointers — they're racing in parallel, not waiting on us.
  const computeMyTeamNextTurn = () => {
    const newTeamTurnIndices = { ...teamTurnIndices }
    const teamLen = (myTeam?.playerIds || []).length || 1
    newTeamTurnIndices[myTeam.id] = (myTeamTurnIdx + 1) % teamLen
    return newTeamTurnIndices
  }

  const handleActorTap = async (actor) => {
    if (usedActorIds.has(actor.id) || committing) return
    if (actor.id === myLastActor?.id) return
    setCommitting(true)
    const movieItem = { type: 'movie', id: selectedMovie.id, title: selectedMovie.title, poster_path: selectedMovie.poster_path ?? null }
    const actorItem = { type: 'actor', id: actor.id, name: actor.name, profile_path: actor.profile_path ?? null }
    const newMyChain = [...myChain, movieItem, actorItem]
    const isGoal = actor.id === goalActor?.id
    const newMoves = { ...(party.playerMoves || {}), [currentUserId]: (party.playerMoves?.[currentUserId] || 0) + 1 }

    const updates = {
      [`h2hChains.${myTeam.id}`]: newMyChain,
      playerMoves: newMoves,
    }
    if (isGoal) {
      updates.status = 'h2h-results'
      updates.h2hWinner = myTeam.id
      updates.finishedAt = new Date()
      updates.solvedAt = new Date()
    } else {
      // Advance ONLY this team's turn pointer — other teams keep racing.
      updates.teamTurnIndices = computeMyTeamNextTurn()
      updates.h2hTurnStartedAt = new Date()
    }
    try { await updateParty(party.id, updates) }
    catch { setCommitting(false) }
  }

  const handlePass = async () => {
    if (committing) return
    setCommitting(true)
    const prevPassed = Array.isArray(party.passedUids) ? party.passedUids : []
    const passedUids = prevPassed.includes(currentUserId) ? prevPassed : [...prevPassed, currentUserId]
    try {
      await updateParty(party.id, {
        teamTurnIndices: computeMyTeamNextTurn(),
        h2hTurnStartedAt: new Date(),
        passedUids,
      })
    } catch { setCommitting(false) }
  }

  // Cameo: try to find a direct connection to goal; else continue chain; else pass
  const handleCameo = async () => {
    if (cameoLoading) return
    setCameoLoading(true)
    try {
      const acts = await getActorMovies(myLastActor.id)
      const candidates = acts.filter(m => !usedMovieIds.has(m.id)).slice(0, 12)
      for (const m of candidates) {
        const c = await getMovieCast(m.id)
        const hit = c.find(a => a.id === goalActor.id)
        if (hit) {
          const newMyChain = [
            ...myChain,
            { type: 'movie', id: m.id, title: m.title, poster_path: m.poster_path ?? null },
            { type: 'actor', id: goalActor.id, name: goalActor.name, profile_path: goalActor.profile_path ?? null },
          ]
          const newMoves = { ...(party.playerMoves || {}), [currentUserId]: (party.playerMoves?.[currentUserId] || 0) + 1 }
          await updateParty(party.id, {
            [`h2hChains.${myTeam.id}`]: newMyChain,
            playerMoves: newMoves,
            cameoLastBy: players[currentUserId]?.username || 'Guest',
            status: 'h2h-results',
            h2hWinner: myTeam.id,
            finishedAt: new Date(),
            solvedAt: new Date(),
          })
          return
        }
      }
      for (const m of candidates) {
        const c = await getMovieCast(m.id)
        const next = c.find(a => !usedActorIds.has(a.id) && a.id !== myLastActor.id && a.profile_path)
        if (next) {
          const newMyChain = [
            ...myChain,
            { type: 'movie', id: m.id, title: m.title, poster_path: m.poster_path ?? null },
            { type: 'actor', id: next.id, name: next.name, profile_path: next.profile_path ?? null },
          ]
          const newMoves = { ...(party.playerMoves || {}), [currentUserId]: (party.playerMoves?.[currentUserId] || 0) + 1 }
          await updateParty(party.id, {
            [`h2hChains.${myTeam.id}`]: newMyChain,
            playerMoves: newMoves,
            cameoLastBy: players[currentUserId]?.username || 'Guest',
            teamTurnIndices: computeMyTeamNextTurn(),
            h2hTurnStartedAt: new Date(),
          })
          return
        }
      }
      await handlePass()
    } catch { setCameoLoading(false) }
  }

  return (
    <div className="h2h-active">
      <div className="h2h-active__chain-bar">
        {myChain.length > 0 ? (
          <Filmstrip chain={myChain} onItemClick={() => {}} hidePlaceholder followLiveEdge />
        ) : (
          <p className="h2h-active__chain-empty">
            Your team's first move. From: <strong>{myLastActor?.name}</strong>
          </p>
        )}
      </div>

      <div className="h2h-active__header">
        <p className="h2h-active__title"><IconClapper size={16} style={{ verticalAlign: 'middle' }} /> Your turn for {myTeam?.name}!</p>
        <p className="h2h-active__sub">
          {view === 'movies'
            ? <>Pick a movie from <strong>{myLastActor?.name}</strong></>
            : <>Pick an actor from <strong>{selectedMovie?.title}</strong></>}
        </p>
        <p className="h2h-active__goal"><IconTarget size={15} style={{ verticalAlign: 'middle' }} /> Goal: <strong>{goalActor?.name}</strong></p>
      </div>

      {view === 'cast' && (
        <button
          type="button"
          className="btn-ghost h2h-active__back"
          onClick={() => { setView('movies'); setSelected(null); setCast([]) }}
        >
          ← Pick a different movie
        </button>
      )}

      {loading && <div className="h2h-active__loading"><div className="spinner" /></div>}
      {error && <p className="h2h-active__error">{error}</p>}

      {!loading && view === 'movies' && (
        <div className="h2h-active__grid">
          {movies.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`h2h-active__card${usedMovieIds.has(m.id) ? ' h2h-active__card--used' : ''}`}
              onClick={() => handleMovieTap(m)}
              disabled={usedMovieIds.has(m.id)}
            >
              <img src={m.poster_path ? posterUrl(m.poster_path) : MOVIE_PH} alt={m.title} />
              <span className="h2h-active__card-name">{m.title}</span>
            </button>
          ))}
        </div>
      )}

      {!loading && view === 'cast' && (
        <div className="h2h-active__grid">
          {cast.map((a) => {
            const isUsed = usedActorIds.has(a.id) || a.id === myLastActor?.id
            const isGoal = a.id === goalActor?.id
            return (
              <button
                key={a.id}
                type="button"
                className={[
                  'h2h-active__card',
                  isUsed ? 'h2h-active__card--used' : '',
                  isGoal ? 'h2h-active__card--goal' : '',
                ].join(' ').trim()}
                onClick={() => handleActorTap(a)}
                disabled={isUsed || committing}
              >
                <img src={a.profile_path ? profileUrl(a.profile_path) : ACTOR_PH} alt={a.name} />
                <span className="h2h-active__card-name">{a.name}{isGoal ? <> <IconTarget size={12} style={{ verticalAlign: 'middle' }} /></> : ''}</span>
              </button>
            )
          })}
        </div>
      )}

      {showCameoPrompt && (
        <div className="coop-cameo-overlay">
          <div className="coop-cameo-dialog">
            <p className="coop-cameo-dialog__title">Lights are waiting... <IconClapper size={16} style={{ verticalAlign: 'middle' }} /></p>
            <p className="coop-cameo-dialog__body">The room is counting on you!</p>
            <div className="coop-cameo-dialog__actions">
              <button className="btn-primary" onClick={handleCameo} disabled={cameoLoading}>
                {cameoLoading ? 'Calling...' : <>Cameo <IconStar size={15} style={{ verticalAlign: 'middle' }} /></>}
              </button>
              <button className="btn-ghost" onClick={handlePass} disabled={committing}>
                Pass <IconMasks size={15} style={{ verticalAlign: 'middle' }} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
